import React from 'react'
import { Package } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { DynamicFieldRenderer } from './DynamicFieldRenderer'
import { TemplateCategoryManager } from './TemplateCategoryManager'
import { useTemplateForm } from '@/hooks/useTemplateForm'
import { MaterialFormData } from './types'

interface TemplateDrivenMaterialInfoSectionProps {
  formData: MaterialFormData
  onInputChange: (field: keyof MaterialFormData, value: string | number | boolean) => void
  errors: Record<string, string>
}

export const TemplateDrivenMaterialInfoSection: React.FC<TemplateDrivenMaterialInfoSectionProps> = ({
  formData,
  onInputChange,
  errors
}) => {
  const {
    formConfig,
    loading,
    error,
    currentTemplate,
    hasField,
    getFieldConfig,
    getFieldOptions,
    getFieldLabel,
    getFieldPlaceholder,
    refreshConfig
  } = useTemplateForm()

  const getTemplatePlaceholder = () => {
    if (!currentTemplate) return "e.g., Material Name"
    
    switch (currentTemplate.businessType) {
      case '3d_printing':
        return "e.g., PLA Filament 1.75mm Red"
      case 'jewelry':
        return "e.g., Sterling Silver Wire 20ga"
      case 'woodworking':
        return "e.g., Oak Hardwood Board 1x6"
      case 'photography':
        return "e.g., Canon EF 50mm f/1.8 Lens"
      case 'automotive':
        return "e.g., Brake Pads Front Set"
      case 'electronics':
        return "e.g., Arduino Uno R3 Board"
      case 'culinary':
        return "e.g., Organic Extra Virgin Olive Oil"
      default:
        return "e.g., Material Name"
    }
  }

  const handleSpecificationChange = (field: string, value: string | number) => {
    const updatedSpecs = {
      ...formData.specifications,
      [field]: value
    }
    onInputChange('specifications' as any, updatedSpecs as any)
  }

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading template configuration...</span>
        </div>
      </Card>
    )
  }

  if (error || !formConfig) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error || 'Failed to load template configuration'}</p>
          <p className="text-gray-600">Please refresh the page or contact support if the problem persists.</p>
        </div>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Package className="w-5 h-5 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Material Information
          {currentTemplate && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({currentTemplate.name} Template)
            </span>
          )}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Universal Fields - Always Present */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Material Name *
          </label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onInputChange('name', e.target.value)
            }
            placeholder={getTemplatePlaceholder()}
            className={errors.name ? 'border-red-500' : ''}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            SKU (Optional)
          </label>
          <Input
            type="text"
            value={formData.sku}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              onInputChange('sku', e.target.value)
            }
            placeholder="e.g., SSW-20GA-001"
          />
        </div>

        {/* Template-Driven Category Selection with Add Category Feature */}
        <TemplateCategoryManager
          categories={formConfig.categories}
          selectedCategory={formData.category}
          onCategoryChange={(value) => onInputChange('category', value)}
          onCategoriesUpdate={refreshConfig}
          error={errors.category}
        />

        {/* Template-Driven Unit Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Base Unit *
          </label>
          <Select
            value={formData.baseUnit}
            onValueChange={(value) => onInputChange('baseUnit', value)}
            className={errors.baseUnit ? 'border-red-500' : ''}
            required
          >
            <option value="">Select base unit...</option>
            {formConfig.unitTypes.map(unit => (
              <option key={unit.id} value={unit.name}>
                {unit.name} ({unit.abbreviation})
              </option>
            ))}
          </Select>
          {errors.baseUnit && (
            <p className="text-red-500 text-sm mt-1">{errors.baseUnit}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Primary unit for inventory tracking and calculations
          </p>
        </div>

        {/* Dynamic Template-Specific Fields */}
        {formConfig.specifications.fields.map(field => {
          const currentValue = formData.specifications[field.name] || ''
          
          return (
            <DynamicFieldRenderer
              key={field.name}
              field={field}
              value={currentValue}
              onChange={(value) => handleSpecificationChange(field.name, value)}
              error={errors[field.name]}
            />
          )
        })}

        {/* Universal Inventory Management Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Reorder Point
          </label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={formData.reorderPoint}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onInputChange('reorderPoint', parseFloat(e.target.value) || 0)
            }
            placeholder="0"
          />
          <p className="text-sm text-gray-500 mt-1">
            Alert when stock falls below this level (in {formData.baseUnit || 'base unit'})
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Maximum Stock
          </label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={formData.maxStock}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onInputChange('maxStock', parseFloat(e.target.value) || 0)
            }
            placeholder="0"
          />
          <p className="text-sm text-gray-500 mt-1">
            Maximum stock level for inventory management (in {formData.baseUnit || 'base unit'})
          </p>
        </div>
      </div>

      {/* Universal Description Field */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description (Optional)
        </label>
        <textarea
          value={formData.description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
            onInputChange('description', e.target.value)
          }
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          placeholder="Additional details about this material..."
        />
      </div>

      {/* Template Information Display */}
      {currentTemplate && (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
            Template Information
          </h4>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            This form is customized for <strong>{currentTemplate.name}</strong> business template.
            Fields and options are tailored for {currentTemplate.industry} industry standards.
          </p>
        </div>
      )}
    </Card>
  )
}